import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import tarefaService from '../../services/tarefa.service';

// ════════════════════════════════════════════════════════════════════════════
// DashboardPage — Fase 7 do MODULO_TAREFAS
// ────────────────────────────────────────────────────────────────────────────
// Substitui o dashboard mock por widgets reais de tarefas:
//   1) 4 metric cards (abertas, urgentes, em andamento, atrasadas)
//   2) "Minhas tarefas" — lista compacta atribuídas a mim, top 8 por deadline
//   3) "Tarefas urgentes" — deadline ≤3d OU prioridade A
//   4) "Apontamentos da semana" — barras seg-dom + total
//
// Layout responsivo: 2 colunas em desktop, 1 em mobile (≤960px).
// Atualiza ao montar e quando troca de usuário.
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',  cinzaSt: '#f3f4f6',
  verde:   '#10b981', verdeBg: '#f0fff4',
  vermelho:'#dc2626', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  // r182: amarelo do status 'Pendente de Retorno' (o COR.amarelo do Pausado
  //       e mais ambar -- os dois precisam ser distinguiveis lado a lado).
  amareloRetorno: '#eab308', amlRetBg: '#fefce8',
  roxo:    '#7c3aed',
  laranja: '#f97316',
  // r220: Pausado virou azul claro e Pendente Cham. Filho virou laranja.
  //       Chaves proprias — COR.azul e COR.laranja seguem valendo pro resto.
  azulClaroPausado: '#38BDF8', azulClaroPausadoBg: '#f0f9ff',
  laranjaFilho:     '#F97316', laranjaFilhoBg:     '#fff7ed',
};

const SETOR = {
  C: { label: 'Comercial',     cor: '#2563eb' },
  T: { label: 'Técnico',        cor: '#7c3aed' },
  Q: { label: 'Qualidade',      cor: '#f97316' },
  A: { label: 'Administrativo', cor: '#6b7280' },
  P: { label: 'Apoio Técnico',  cor: '#db2777' },
  O: { label: 'Outros',         cor: '#06b6d4' },
};

const STATUS_EXECUCAO = {
  N: { label: 'Não iniciado',  cor: COR.muted   },
  E: { label: 'Em andamento',   cor: COR.verde  },
  X: { label: 'Pausado',         cor: COR.azulClaroPausado },
  R: { label: 'Pendente de retorno', cor: COR.amareloRetorno },
  P: { label: 'Pendente',        cor: COR.laranjaFilho },
  F: { label: 'Finalizado',      cor: COR.vermelho },
  S: { label: 'Sandbox',         cor: COR.laranja },
};

const PRIORIDADE = {
  A: { label: 'Alta',  cor: COR.vermelho, icone: '🔴' },
  M: { label: 'Média', cor: COR.amarelo,  icone: '🟡' },
  B: { label: 'Baixa', cor: COR.verde,    icone: '🟢' },
};

function formatarData(d) {
  if (!d) return '';
  const data = new Date(d);
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function diasAteDeadline(deadline) {
  if (!deadline) return null;
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const dl = new Date(deadline); dl.setHours(0, 0, 0, 0);
  return Math.round((dl - hoje) / (1000 * 60 * 60 * 24));
}

// ─── <MetricCard> ────────────────────────────────────────────────────────
function MetricCard({ label, valor, sublabel, cor, icone, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`,
        borderLeft: `4px solid ${cor || COR.azul}`,
        borderRadius: '8px',
        padding: '14px 16px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.1s, box-shadow 0.1s',
      }}
      onMouseEnter={e => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '11px', color: COR.muted, marginBottom: '6px',
                        textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>
            {label}
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: cor || COR.texto, lineHeight: 1 }}>
            {valor}
          </div>
          {sublabel && (
            <div style={{ fontSize: '11px', color: COR.muted, marginTop: '6px' }}>
              {sublabel}
            </div>
          )}
        </div>
        {icone && (
          <div style={{ fontSize: '24px', opacity: 0.6 }}>{icone}</div>
        )}
      </div>
    </div>
  );
}

// ─── <CardTarefaCompacto> ────────────────────────────────────────────────
// Linha enxuta pra ser usada nas listas dos widgets. Click navega pra tarefa.
function CardTarefaCompacto({ tarefa, onClick }) {
  const setor = SETOR[tarefa.setor] || SETOR.O;
  const status = STATUS_EXECUCAO[tarefa.status_execucao] || STATUS_EXECUCAO.N;
  const prio = PRIORIDADE[tarefa.prioridade] || PRIORIDADE.M;
  const diasDl = diasAteDeadline(tarefa.deadline);
  const atrasada = diasDl !== null && diasDl < 0;
  const proxima = diasDl !== null && diasDl >= 0 && diasDl <= 3;

  return (
    <div
      onClick={onClick}
      style={{
        padding: '8px 10px',
        borderBottom: `1px solid ${COR.borda}`,
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: '8px',
        fontSize: '12px',
      }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = COR.cinzaBg}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
    >
      {/* Bolinha de status */}
      <div title={status.label} style={{
        width: '8px', height: '8px', borderRadius: '50%',
        backgroundColor: status.cor, flexShrink: 0,
      }} />

      {/* ID + título */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '10px', color: COR.muted, fontFamily: 'monospace', flexShrink: 0 }}>
            #{tarefa.id}
          </span>
          <span style={{
            fontWeight: '600', color: COR.texto,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            flex: 1,
          }} title={tarefa.titulo}>
            {tarefa.titulo}
          </span>
        </div>
        {(tarefa.cliente_fantasia || tarefa.negocio_descricao) && (
          <div style={{
            fontSize: '10px', color: COR.muted, marginTop: '2px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {tarefa.cliente_fantasia || tarefa.cliente_nome}
            {tarefa.negocio_descricao && tarefa.cliente_fantasia && ' · '}
            {tarefa.negocio_descricao}
          </div>
        )}
      </div>

      {/* Tag de setor */}
      <span style={{
        padding: '2px 7px', borderRadius: '4px',
        backgroundColor: setor.cor + '20', color: setor.cor,
        fontSize: '9px', fontWeight: '700', letterSpacing: '0.3px',
        textTransform: 'uppercase',
        flexShrink: 0,
      }}>
        {setor.label.substring(0, 4)}
      </span>

      {/* Prioridade Alta marcada */}
      {tarefa.prioridade === 'A' && (
        <span style={{
          fontSize: '11px', flexShrink: 0,
        }} title="Prioridade Alta">{prio.icone}</span>
      )}

      {/* Deadline */}
      {tarefa.deadline && (
        <span style={{
          fontSize: '10px', fontWeight: '600',
          color: atrasada ? COR.vermelho : (proxima ? COR.amarelo : COR.muted),
          flexShrink: 0,
        }}>
          {atrasada ? `⚠ ${Math.abs(diasDl)}d atraso` :
           diasDl === 0 ? 'Hoje' :
           diasDl === 1 ? 'Amanhã' :
           proxima ? `Em ${diasDl}d` :
           formatarData(tarefa.deadline)}
        </span>
      )}
    </div>
  );
}

// ─── <Widget> — card container padrão ────────────────────────────────────
function Widget({ titulo, icone, contador, children, acaoLabel, onAcao }) {
  return (
    <div style={{
      backgroundColor: COR.branco,
      border: `1px solid ${COR.borda}`,
      borderRadius: '8px',
      padding: '0',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${COR.borda}`,
        display: 'flex', alignItems: 'center', gap: '8px',
        backgroundColor: COR.cinzaBg,
      }}>
        {icone && <span style={{ fontSize: '14px' }}>{icone}</span>}
        <span style={{
          fontSize: '13px', fontWeight: '700', color: COR.texto,
          fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
          textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>
          {titulo}
        </span>
        {contador !== undefined && (
          <span style={{
            backgroundColor: COR.azul, color: COR.branco,
            fontSize: '10px', fontWeight: '700',
            padding: '1px 8px', borderRadius: '10px',
            marginLeft: '4px',
          }}>{contador}</span>
        )}
        {onAcao && (
          <span
            onClick={onAcao}
            style={{
              marginLeft: 'auto', fontSize: '11px', color: COR.azul,
              cursor: 'pointer', fontWeight: '600',
            }}>
            {acaoLabel || 'Ver todas →'}
          </span>
        )}
      </div>
      <div style={{ padding: '4px 0' }}>
        {children}
      </div>
    </div>
  );
}

// ─── <BarChart> — gráfico de barras (HTML/CSS, sem distorção) ────────────
function BarChart({ dados, totalSemana }) {
  if (!dados || dados.length === 0) return null;

  const max = Math.max(...dados.map(d => d.horas), 8);  // mínimo 8h pra escala
  const ALTURA_AREA = 160;  // px da área de barras

  const hoje = new Date();
  const hojeStr = hoje.toISOString().slice(0, 10);

  return (
    <div style={{ padding: '14px 18px' }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: '14px',
      }}>
        <span style={{ fontSize: '11px', color: COR.muted, fontWeight: '600' }}>Total da semana</span>
        <span style={{ fontSize: '24px', fontWeight: '700', color: COR.azul }}>
          {totalSemana.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}h
        </span>
      </div>

      {/* Container flex com 7 colunas iguais */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        height: `${ALTURA_AREA}px`,
        gap: '8px',
        borderBottom: `1px solid ${COR.borda}`,
        paddingBottom: '0',
      }}>
        {dados.map((d, i) => {
          const dataStr = (d.data instanceof Date) ? d.data.toISOString().slice(0, 10)
                        : String(d.data).slice(0, 10);
          const ehHoje = dataStr === hojeStr;
          const cor = ehHoje ? COR.azul : (d.horas > 0 ? COR.azul + 'cc' : COR.cinzaSt);
          // Altura proporcional. Mín de 2px pra dias sem horas verem que existe a coluna.
          const alturaPct = d.horas > 0 ? Math.max((d.horas / max) * 100, 4) : 1;

          return (
            <div key={i} style={{
              flex: 1,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'flex-end',
              height: '100%',
            }}>
              {/* Valor em cima da barra */}
              <div style={{
                fontSize: '11px', fontWeight: '700', color: COR.texto,
                marginBottom: '4px',
                visibility: d.horas > 0 ? 'visible' : 'hidden',
              }}>
                {d.horas.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
              </div>
              {/* Barra */}
              <div style={{
                width: '100%', maxWidth: '60px',
                height: `${alturaPct}%`,
                backgroundColor: cor,
                borderRadius: '4px 4px 0 0',
                transition: 'background-color 0.15s',
              }} />
            </div>
          );
        })}
      </div>

      {/* Labels dos dias (linha separada, alinhada com as colunas) */}
      <div style={{
        display: 'flex', gap: '8px', marginTop: '6px',
      }}>
        {dados.map((d, i) => {
          const dataStr = (d.data instanceof Date) ? d.data.toISOString().slice(0, 10)
                        : String(d.data).slice(0, 10);
          const ehHoje = dataStr === hojeStr;
          return (
            <div key={i} style={{
              flex: 1, textAlign: 'center',
              fontSize: '11px', fontWeight: ehHoje ? '700' : '500',
              color: ehHoje ? COR.azul : COR.muted,
              fontFamily: "'Manrope', sans-serif",
            }}>
              {d.dia}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Helper: detecta mobile (viewport ≤ 960px) ──────────────────────────
function useEhMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth <= 960);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= 960);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
}

// ════════════════════════════════════════════════════════════════════════════
// DashboardPage — componente principal
// ════════════════════════════════════════════════════════════════════════════
function DashboardPage() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const ehMobile = useEhMobile();

  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    if (!usuario?.id) return;
    try {
      setCarregando(true);
      setErro('');
      const r = await tarefaService.dashboard(usuario.id);
      setDados(r);
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
      setErro('Falha ao carregar dados do dashboard.');
    } finally {
      setCarregando(false);
    }
  }, [usuario]);

  useEffect(() => { carregar(); }, [carregar]);

  const hoje = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const irParaTarefas = (tarefa_id) => {
    if (tarefa_id) navigate(`/tarefas?tarefa_id=${tarefa_id}`);
    else           navigate('/tarefas');
  };

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Cabeçalho */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: COR.texto, margin: 0 }}>
          Bem-vindo, {usuario?.nomeAmigavel || 'Usuário'}!
        </h1>
        <p style={{ fontSize: '12px', color: COR.muted, marginTop: '4px', textTransform: 'capitalize' }}>
          {hoje}
        </p>
      </div>

      {erro && (
        <div style={{
          padding: '10px 14px', marginBottom: '12px',
          backgroundColor: COR.vermBg, color: COR.vermelho,
          border: `1px solid ${COR.vermelho}33`,
          borderRadius: '6px', fontSize: '12px', fontWeight: '600',
        }}>{erro}</div>
      )}

      {carregando && !dados ? (
        <div style={{
          padding: '40px', textAlign: 'center',
          color: COR.muted, fontSize: '13px',
        }}>
          Carregando dashboard...
        </div>
      ) : dados ? (
        <>
          {/* ── Linha 1: 4 metric cards ───────────────────────────────── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: ehMobile
              ? 'repeat(2, 1fr)'
              : 'repeat(4, 1fr)',
            gap: '12px',
            marginBottom: '16px',
          }}>
            <MetricCard
              label="Tarefas abertas"
              valor={dados.contadores.abertas}
              sublabel="Atribuídas a mim"
              icone="📋"
              cor={COR.azul}
              onClick={() => irParaTarefas()}
            />
            <MetricCard
              label="Urgentes"
              valor={dados.contadores.urgentes}
              sublabel="Deadline ≤3d ou Alta"
              icone="⚠"
              cor={COR.vermelho}
              onClick={() => irParaTarefas()}
            />
            <MetricCard
              label="Em andamento"
              valor={dados.contadores.em_andamento}
              sublabel="Status: Em andamento"
              icone="▶"
              cor={COR.verde}
              onClick={() => irParaTarefas()}
            />
            <MetricCard
              label="Atrasadas"
              valor={dados.contadores.atrasadas}
              sublabel="Deadline vencido"
              icone="⏰"
              cor={COR.amarelo}
              onClick={() => irParaTarefas()}
            />
          </div>

          {/* ── Linha 2: 3 widgets (responsive grid) ──────────────────── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: ehMobile
              ? '1fr'
              : '1fr 1fr',
            gap: '12px',
            marginBottom: '12px',
          }}>
            {/* Minhas tarefas */}
            <Widget
              icone="📋"
              titulo="Minhas tarefas"
              contador={dados.minhas.length}
              acaoLabel="Ver todas →"
              onAcao={() => irParaTarefas()}
            >
              {dados.minhas.length === 0 ? (
                <div style={{
                  padding: '24px', textAlign: 'center',
                  color: COR.muted, fontSize: '12px', fontStyle: 'italic',
                }}>
                  Nenhuma tarefa atribuída a você no momento.
                </div>
              ) : (
                dados.minhas.map(t => (
                  <CardTarefaCompacto
                    key={t.id}
                    tarefa={t}
                    onClick={() => irParaTarefas(t.id)}
                  />
                ))
              )}
            </Widget>

            {/* Tarefas urgentes */}
            <Widget
              icone="🚩"
              titulo="Tarefas urgentes"
              contador={dados.urgentes.length}
              acaoLabel="Ver todas →"
              onAcao={() => irParaTarefas()}
            >
              {dados.urgentes.length === 0 ? (
                <div style={{
                  padding: '24px', textAlign: 'center',
                  color: COR.muted, fontSize: '12px', fontStyle: 'italic',
                }}>
                  Sem urgências! 🎉
                </div>
              ) : (
                dados.urgentes.map(t => (
                  <CardTarefaCompacto
                    key={t.id}
                    tarefa={t}
                    onClick={() => irParaTarefas(t.id)}
                  />
                ))
              )}
            </Widget>
          </div>

          {/* ── Linha 3: Apontamentos da semana (largura cheia) ────────── */}
          <Widget icone="⏱" titulo="Apontamentos da semana">
            <BarChart
              dados={dados.apontamentos_semana}
              totalSemana={dados.total_horas_semana}
            />
          </Widget>
        </>
      ) : null}
    </div>
  );
}

export default DashboardPage;
